import { Link, useLocation } from '@solidjs/router';
import { gql } from 'graphql-request';
import { useContext, Show, For } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Text } from '../../../ui-components/text/text';
import { Column } from '../../../grid-system/column/column';
import { Grid } from '../../../grid-system/grid/grid';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { removeTrailingSlash } from '../../../tools/remove-trailing-slash';
import { NewAtosLogoWhite } from '../../icons-library/new-atos-logo-white';
import { Button } from '../../../ui-components/button/button';
import { TwitterIcon } from '../../icons-library/social-media/twitter';
import { FacebookIcon } from '../../icons-library/social-media/facebook';
import { LinkedinIcon } from '../../icons-library/social-media/linkedin';
import { InstagramIcon } from '../../icons-library/social-media/instagram';
import { YoutubeIconPlain } from '../../icons-library/youtube-icon-plain';


const GET_FOOTER_QUERY = gql`
    query GetFooter($key: String!) {
        footer: resource(key: $key) {
            content
            key
        }
    }
`;

export const FooterAtosCareContainer = () => {

    const { createCachedResource, localize } = useContext(AppContext);
    const [ footerContent ] = createCachedResource(GET_FOOTER_QUERY, () => ({ key: '/wp-json/rawb/v1/footer' }), true);
    const location = useLocation();

    const footer = () => footerContent()?.footer?.content;

    const parsedCopyrightText = () => {
        const text = footerContent()?.footer?.content?.copyrighttext;
        if (!text) return '';
        const match = text.match(/\{\{(.*?)\}\}/);
        if (!match) return text;
        const currentYear = new Date().getFullYear();
        return text.replace(match[0], currentYear.toString());
    };

    const handleOnClickCookieConsent = (e: any) => {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.CookieConsent.renew();
    };

    return (
        <ErrorCatcher componentName="Footer">
            <footer id="site-footer" class="[container-type:inline-size] min-h-[32.14rem] bg-[var(--component-footer-default-bg-color)] text-cvi-fg-on-color flex flex-col justify-between max-[600px]:h-auto print:hidden">
                <Grid templateShorthand={[3, 3, 3, 3]} isLayoutGrid={true} customCss={'padding-block: 6.63rem'}>
                    <div class="w-auto pb-[2rem] max-[600px]:flex max-[600px]:items-center max-[600px]:flex-col">
                        <NewAtosLogoWhite />
                    </div>

                    <div class="pb-[1rem] leading-[0] max-[600px]:flex max-[600px]:items-center max-[600px]:flex-col max-[600px]:text-center max-[600px]:[&>*]:max-w-[300px]">
                        <Text fontSize="footerHeading" color={'white'} displayRedVerticalLine={false} fontStyle="bold">
                            {footer()?.column?.header}
                        </Text>
                        <For each={footer()?.column?.columnItems}>
                            {(columnItem) => {
                                if (!columnItem || !columnItem.title || !columnItem.url) {
                                    return null;
                                }

                                return (
                                    <div class="mb-[0.5rem]">
                                        <Link href={removeTrailingSlash(columnItem.url)} target={columnItem.target}>
                                            <Text
                                                fontSize={'footerSmall'}
                                                color={'white'}
                                                noBlockSpacing={true}
                                                displayRedVerticalLine={false}
                                                backgroundColor="transparent"
                                            >
                                                {columnItem.title}
                                            </Text>
                                        </Link>
                                    </div>
                                );
                            }}
                        </For>

                        <Link href={location.pathname} onClick={handleOnClickCookieConsent}>
                            <Text fontSize={'footerSmall'} color={'white'} noBlockSpacing={true} displayRedVerticalLine={false} backgroundColor="transparent">
                                {localize('cookie-consent', 'Cookie consent')}
                            </Text>
                        </Link>
                    </div>

                    <div class="pb-[1rem] leading-[0] max-[600px]:flex max-[600px]:items-center max-[600px]:flex-col max-[600px]:text-center max-[600px]:[&>*]:max-w-[300px] max-[600px]:flex-col-reverse max-[600px]:[&>div+div]:mt-[1rem]">
                        <Show when={!footer()?.openingHours?.hide && footer()?.openingHours?.text}>
                            <div class="mb-[3.5rem] max-[600px]:mb-0">
                                <Text fontSize="footerHeading" color={'white'} displayRedVerticalLine={false} fontStyle="bold">
                                    {footer()?.openingHours?.heading}
                                </Text>
                                <Text fontSize="footerSmall" color={'white'} displayRedVerticalLine={false}>
                                    {footer()?.openingHours?.text}
                                </Text>
                            </div>
                        </Show>

                        <Show when={footer()?.socialMedia && !footer()?.socialMedia?.hide}>
                            <div>
                                <Text fontSize="footerHeading" color={'white'} displayRedVerticalLine={false} fontStyle="bold">
                                    {footer()?.socialMedia?.heading}
                                </Text>
                                <div class="flex justify-start -ml-[0.7rem] flex-wrap [&>*]:opacity-80 [&>*]:px-[0.7rem] [&>*]:py-[0.4rem] [&>*]:transition-opacity [&>*]:duration-200 [&>*:hover]:opacity-100 max-[600px]:justify-center max-[600px]:mb-[3.5rem]">
                                    <Show when={footer()?.socialMedia?.linkedin}>
                                        <Link target="_blank" href={footer()?.socialMedia?.linkedin}>
                                            <LinkedinIcon width={1.8} height={1.8} />
                                        </Link>
                                    </Show>
                                    <Show when={footer()?.socialMedia?.facebook}>
                                        <Link target="_blank" href={footer()?.socialMedia?.facebook}>
                                            <FacebookIcon width={1.8} height={1.8} />
                                        </Link>
                                    </Show>
                                    <Show when={footer()?.socialMedia?.instagram}>
                                        <Link target="_blank" href={footer()?.socialMedia?.instagram}>
                                            <InstagramIcon width={1.8} height={1.8} />
                                        </Link>
                                    </Show>
                                    <Show when={footer()?.socialMedia?.twitter}>
                                        <Link target="_blank" href={footer()?.socialMedia?.twitter}>
                                            <TwitterIcon width={1.8} height={1.8} />
                                        </Link>
                                    </Show>
                                    <Show when={footer()?.socialMedia?.youtube}>
                                        <Link target="_blank" href={footer()?.socialMedia?.youtube}>
                                            <YoutubeIconPlain width={1.8} height={1.8} />
                                        </Link>
                                    </Show>
                                </div>
                            </div>
                        </Show>
                    </div>

                    <div class="pb-[1rem] leading-[0] max-[600px]:flex max-[600px]:items-center max-[600px]:flex-col max-[600px]:text-center max-[600px]:[&>*]:max-w-[300px]">
                        <Show when={!footer()?.newsletter?.hide && footer()?.newsletter?.link}>
                            <Text fontSize="footerHeading" color={'white'} displayRedVerticalLine={false} fontStyle="bold">
                                {footer()?.newsletter?.heading}
                            </Text>
                            <div class="mb-[1.25rem]">
                                <Text fontSize="footerSmall" color={'white'} displayRedVerticalLine={false} noBlockSpacing={true}>
                                    {footer()?.newsletter?.text}
                                </Text>
                            </div>
                            <Button isLink={true} target={footer()?.newsletter?.link?.url} variant="primary" label={footer()?.newsletter?.link?.title} small={true} darkMode={true} />
                        </Show>
                    </div>
                </Grid>

                <Grid templateShorthand={[12]} customCss={'height: 20%'} isLayoutGrid={true}>
                    <div class="border-t border-cvi-border-on-color py-[2rem]">
                        <Column>
                            {parsedCopyrightText() && (
                                <div class="flex justify-start max-[600px]:justify-center">
                                    <Text align="center" fontSize="small" color="white" displayRedVerticalLine={false}>
                                        {`${localize('copyright', 'Copyright')} ${parsedCopyrightText()}`}
                                    </Text>
                                </div>
                            )}
                        </Column>
                    </div>
                </Grid>
            </footer>
        </ErrorCatcher>
    );
};
